/**
 * Stacked Panels chart — small multiples: one line series per panel, panels
 * stacked vertically sharing a single X axis, each panel with its own Y axis.
 *
 * Built for showing several measures with different units/scales over the same
 * X (e.g. metal / tonnage / grade by period): cramming them onto two overlaid
 * axes is unreadable, so each measure gets its own panel. A series picks its
 * panel via `series[].panel` (0-based, 0 = top); per-panel Y titles come from
 * `options.panels[panel].label`. Within a panel, color/dash distinguish series
 * (e.g. LP solid vs CP dashed). Series-driven: no `series[]` → nothing drawn.
 *
 * Every line renders as a smoothed spline (unconditional — no opt-out flag:
 * this chart type has one real consumer today, the tonnage/grade/metal
 * small-multiples, which always wants smoothed curves). `series[].markers`
 * draws a dot at each point, same convention as `line`/`distribution`
 * (`shared/traces.ts`).
 *
 * The panel layout (domains, shared X anchored to the bottom panel) lives in
 * `stackedPanelsLayout`; every series is rendered as a line.
 */

import type { ChartTypeDefinition, ChartTrace } from '../types';
import { registerChart } from '../registry';
import { xColumn } from '../shared/encoding';
import { makeErrorY } from '../shared/traces';
import { panelAxisId, stackedPanelsLayout } from '../shared/layout';
import { figureScale } from '../theme/scale';
import { DEFAULT_MARKER_SIZE, SERIES_LINE_WIDTH } from '../theme/layout';
import { colorAt } from '../theme/palette';

export const stackedPanelsChart: ChartTypeDefinition = {
  id: 'stackedPanels',
  label: 'Stacked Panels',
  capabilities: {
    multiSeries: true,
    supportsXKey: true,
    supportsYKey: false,
    supportsGroupBy: false,
    supportsLimits: false,
    filterable: true,
    supportsStacked: false,
    legendByDefault: true,
    supportsLineStyle: true,
  },
  buildTraces(model, ctx) {
    const { data, encoding, series, options } = model;
    if (series.length === 0) return [];
    const x = xColumn(data, encoding.xKey);
    const scale = figureScale(ctx.figureWidth);
    const markerSize = options?.markerSize ?? DEFAULT_MARKER_SIZE;
    // Series sharing a `legendGroup` collapse to one legend entry (named by the
    // group), shown once — otherwise the same model repeated across panels
    // would list its color once per panel.
    const seenGroups = new Set<string>();
    return series.map((s, i): ChartTrace => {
      const color = s.color || colorAt(i, ctx.palette);
      const group = s.legendGroup;
      const legend = group
        ? { name: group, legendgroup: group, showlegend: !seenGroups.has(group) }
        : { name: s.name };
      if (group) seenGroups.add(group);
      return {
        x,
        y: data.map((d) => d[s.dataKey]),
        ...legend,
        ...makeErrorY(data, s.errorKey),
        type: 'scatter',
        mode: s.markers ? 'lines+markers' : 'lines',
        line: {
          color,
          width: SERIES_LINE_WIDTH * scale,
          dash: s.dash ?? 'solid',
          shape: 'spline',
        },
        ...(s.markers ? { marker: { color, size: markerSize * scale } } : {}),
        yaxis: panelAxisId(s.panel ?? 0),
      };
    });
  },
  buildLayout(model, ctx) {
    return stackedPanelsLayout(model, ctx);
  },
};

registerChart(stackedPanelsChart);
